import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import Navbar from '../components/Navbar';
import { dishService } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import BulkTranslationSection from '../components/BulkTranslationSection';
import DeleteDishModal from '../components/DeleteDishModal';
import '../styles/TranslationQueue.css';

const TranslationQueuePage = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedItem, setSelectedItem] = useState(null);
  const [translations, setTranslations] = useState({});
  const [initError, setInitError] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const currentSchoolId = useSelector((state) => state.language?.currentSchoolId || null);
  const [activeTab, setActiveTab] = useState('individual');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  const selectedLanguages = useSelector(state => state.language.selectedLanguages || []);
  const defaultLanguageCode = useSelector(state => state.language.defaultLanguageCode || 'en');

  const getSchoolId = useCallback(() => {
    if (currentSchoolId) return currentSchoolId;
    if (userDetails?.current_school?.id) return userDetails.current_school.id;
    return null;
  }, [currentSchoolId, userDetails]);
  
  const schoolId = getSchoolId();
  
  const selectedCodesString = useMemo(() => {
    return (selectedLanguages && selectedLanguages.length > 0)
      ? selectedLanguages.map(lang => lang.code).join(',')
      : null;
  }, [selectedLanguages]);
  
  const schoolSelectedLangs = useMemo(() => 
    new Set((selectedLanguages || []).map(l => l.code)), 
  [selectedLanguages]); 
  
  const queryKey = useMemo(() => ['missingTranslations', schoolId, selectedCodesString], [schoolId, selectedCodesString]);

  const { 
    data: items = [],
    isLoading: loading,
    isError, 
    error: fetchError,
    refetch
  } = useQuery({
    queryKey: queryKey, 
    queryFn: async () => {
      console.log(`[QueueUseQuery - START] Fetching missing translations for school ${schoolId}, selectedCodes: ${selectedCodesString} at ${new Date().toISOString()}`);
      try {
        const data = await dishService.getMissingTranslations(schoolId, selectedCodesString);
        console.log(`[QueueUseQuery - SUCCESS] Received ${data?.length ?? 0} missing translations data at ${new Date().toISOString()}`);
        // Log first few items if they exist
        if (data && data.length > 0) {
           console.log('[QueueUseQuery - Data Sample]', data.slice(0, 3).map(item => ({ id: item.id, type: item.type, missing: item.missingLanguages }) ));
        }
        if (!Array.isArray(data)) {
          console.warn('[QueueUseQuery - WARN] API did not return an array.');
          return [];
        }
        return data;
      } catch (error) {
        console.error(`[QueueUseQuery - ERROR] Error fetching missing translations:`, error);
        throw error; // Re-throw error for React Query to handle
      }
    },
    enabled: !!schoolId && !!selectedCodesString, // Ensure both are available
    // Optional: Add onSuccess/onError for logging data changes
    onSuccess: (data) => {
       console.log(`[QueueUseQuery - OnSuccess] Query succeeded, data length: ${data?.length ?? 0} at ${new Date().toISOString()}`);
    },
    onError: (error) => {
       console.error(`[QueueUseQuery - OnError] Query failed: ${error.message} at ${new Date().toISOString()}`);
    }
  });
  
  const stats = {
    total: items.length,
    completed: (/* Some logic to track completed items if needed */ 0) 
  };
  
  useEffect(() => {
    console.log(`[QueueEffect - ItemsChanged] Running effect due to items change. Items length: ${items?.length ?? 'undefined'} at ${new Date().toISOString()}`);
    if (items && items.length > 0) {
        console.log('[QueueEffect - ItemsChanged] First 3 items:', items.slice(0, 3).map(item => ({ id: item.id, type: item.type, missing: item.missingLanguages }) ));
    }
    
    if (!loading && !isError && items.length > 0 && !selectedItem) {
      console.log(`[QueueEffect - ItemsChanged] Selecting first item: ID ${items[0]?.id} at ${new Date().toISOString()}`);
      setSelectedItem(items[0]);
    } else if (items.length === 0 && selectedItem) { // Only clear if there was a selected item
      console.log(`[QueueEffect - ItemsChanged] No items, clearing selection at ${new Date().toISOString()}`);
      setSelectedItem(null);
      setTranslations({});
    } else {
       console.log(`[QueueEffect - ItemsChanged] Condition not met for selecting first item or clearing selection. Loading: ${loading}, IsError: ${isError}, ItemsLength: ${items.length}, SelectedItem: ${!!selectedItem}`);
    }
  }, [items, loading, isError, selectedItem]); // Keep dependencies as they are
  
  const initializeTranslations = useCallback((item) => {
    console.log('[initializeTranslations] Initializing for item:', item?.id);
    if (!item) return;
    
    const newTranslations = {};
    
    if (item.allTranslations) {
      Object.entries(item.allTranslations).forEach(([lang, value]) => {
        newTranslations[lang] = value || '';
      });
    }
    
    item.missingLanguages.forEach(lang => {
      if (!newTranslations[lang]) {
        newTranslations[lang] = '';
      }
    });
    
    console.log('Setting initial translations:', newTranslations);
    setTranslations(newTranslations);
  }, []);
  
  useEffect(() => {
    try {
      console.log('Initializing Translation Queue page...');
      setInitError(null);
      const userData = localStorage.getItem('user');
      
      if (userData) {
        const parsedUser = JSON.parse(userData);
        console.log('School ID from user data:', parsedUser.current_school?.id);
        console.log('School ID from subdomain context:', currentSchoolId);
        setUserDetails(parsedUser);
      } else {
        console.warn('No user data found in localStorage');
        setInitError('You must be logged in to view this page');
      }
    } catch (err) {
      console.error('Error parsing user data from localStorage:', err);
      setInitError('Error loading user data: ' + err.message);
    }
  }, [currentSchoolId]);
  
  const handleSelectItem = useCallback((item) => {
    console.log('[handleSelectItem] Selecting item:', item?.id);
    setSelectedItem(item);
    saveMutation.reset();
    initializeTranslations(item);
  }, [initializeTranslations]);
  
  const handleTranslationChange = useCallback((lang, value) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: value
    }));
  }, []);
  
  const languageNameMap = useMemo(() => {
    return (selectedLanguages || []).reduce((acc, lang) => {
      acc[lang.code] = lang.name;
      return acc;
    }, {});
  }, [selectedLanguages]);

  const getLanguageName = useCallback((code) => {
    return languageNameMap[code] || t(`languageNames.${code}`);
  }, [languageNameMap, t]);
  
  const isAtLeastOneTranslationFilled = useCallback(() => {
    if (!selectedItem) return false;
    
    return selectedItem.missingLanguages.some(lang => 
      translations[lang] && translations[lang].trim() !== ''
    );
  }, [selectedItem, translations]);
  
  const getDisplayType = useCallback((type) => {
    return type === 'dish' ? t('admin.dish') : t('admin.specialName');
  }, [t]);
  
  const getDisplayName = useCallback((item) => {
    if (!item) return '';

    const allTrans = item.allTranslations || {};
    const uiLang = i18n.language;
    const schoolDefaultLang = defaultLanguageCode;
    // Use the top-level memoized Set
    // const schoolSelectedLangs = useMemo(() => new Set((selectedLanguages || []).map(l => l.code)), [selectedLanguages]); 

    const isSelectedLang = (code) => schoolSelectedLangs.has(code);

    // 1. UI Language (if selected for school and translation exists)
    if (isSelectedLang(uiLang) && allTrans[uiLang]) {
      return allTrans[uiLang];
    }

    // 2. School Default Language (if translation exists and is selected)
    if (isSelectedLang(schoolDefaultLang) && allTrans[schoolDefaultLang]) {
      return allTrans[schoolDefaultLang];
    }

    // 3. Other Selected Languages (sorted alphabetically for consistency)
    const otherSelectedLangs = Array.from(schoolSelectedLangs)
                                   .filter(code => code !== uiLang && code !== schoolDefaultLang)
                                   .sort();
    for (const code of otherSelectedLangs) {
      if (allTrans[code]) {
        return allTrans[code];
      }
    }

    // 4. Fallback to English (item.default_name) if no selected language translation found
    if (item.default_name) {
      return item.default_name;
    }

    // 5. Absolute Fallback: Any available translation (sorted by lang code for consistency)
    const firstAvailableTranslation = Object.entries(allTrans)
                                          .sort(([langA], [langB]) => langA.localeCompare(langB))
                                          .find(([_, name]) => !!name);
    if (firstAvailableTranslation) {
        console.warn(`[getDisplayName] Item ${item.id}: Falling back to first available translation (${firstAvailableTranslation[0]}) as no selected language or default_name was found.`);
        return firstAvailableTranslation[1];
    }

    // 6. Generic Fallback String
    console.warn(`[getDisplayName] Item ${item.id}: No suitable display name found. Using generic fallback.`);
    return `${getDisplayType(item.type || (item.is_special_name ? 'specialName' : 'dish'))} #${item.id}`;

  }, [i18n.language, defaultLanguageCode, schoolSelectedLangs, getDisplayType]);
  
  const saveMutation = useMutation({ 
    mutationFn: dishService.saveTranslations,
    onMutate: async (variables) => {
      console.log(`[Mutation] onMutate for item ID: ${variables.itemId}`);
      await queryClient.cancelQueries({ queryKey: queryKey });

      const previousItems = queryClient.getQueryData(queryKey);
      console.log('[Mutation] Previous items snapshot:', previousItems);

      const currentTranslations = translations;
      const translationsToSave = {};
      selectedItem.missingLanguages.forEach(langCode => {
        if (currentTranslations[langCode] && currentTranslations[langCode].trim()) {
          translationsToSave[langCode] = currentTranslations[langCode].trim();
        }
      });

      const remainingMissingLanguages = selectedItem.missingLanguages.filter(langCode => {
        return !translationsToSave.hasOwnProperty(langCode);
      });
      const isNowComplete = remainingMissingLanguages.length === 0;
      console.log(`[Mutation] isNowComplete check: ${isNowComplete}.`);

      if (isNowComplete) {
        const updatedItems = previousItems.filter(item => item.id !== variables.itemId);
        console.log(`[Mutation] Optimistically setting query data with ${updatedItems.length} items.`);
        queryClient.setQueryData(queryKey, updatedItems);

        const currentIndex = previousItems.findIndex(item => item.id === variables.itemId);
        let nextItem = null;
        if (updatedItems.length > 0) {
          const nextIndex = currentIndex < updatedItems.length ? currentIndex : 0;
          nextItem = updatedItems[nextIndex];
        }
        console.log(`[Mutation] Selecting next item optimistically:`, nextItem?.id);
      } else {
        console.log(`[Mutation] Item ${variables.itemId} still has missing languages. No optimistic removal.`);
      }

      return { previousItems, isNowComplete };
    },
    onError: (err, variables, context) => {
      console.error(`[Mutation] onError for item ID: ${variables.itemId}`, err);
      if (context?.previousItems) {
        console.log('[Mutation] Rolling back optimistic update.');
        queryClient.setQueryData(queryKey, context.previousItems);
      }
    },
    onSettled: (data, error, variables, context) => {
      console.log(`[Mutation] onSettled for item ID: ${variables.itemId}. Error: ${!!error}`);
      
      if (!error && context?.isNowComplete) {
          const currentItems = queryClient.getQueryData(queryKey) || []; 
          
          const previousItems = context.previousItems || [];
          const currentIndex = previousItems.findIndex(item => item.id === variables.itemId);
          
          let nextItem = null;
          if (currentItems.length > 0) {
            const nextIndex = (currentIndex >= 0 && currentIndex < currentItems.length) ? currentIndex : 0; 
            nextItem = currentItems[nextIndex];
          }
          console.log(`[Mutation] Selecting next item post-settle:`, nextItem?.id);
          handleSelectItem(nextItem);
      }
    },
    onSuccess: (data, variables, context) => {
        console.log(`[Mutation] onSuccess for item ID: ${variables.itemId}. Received data:`, data);
        
        // Handle new completion checker response format
        if (data?.queueUpdates) {
          console.log(`[Mutation] Processing queue updates:`, data.queueUpdates);
          
          // Remove from translation queue if now complete
          if (data.queueUpdates.removeFromTranslations) {
            console.log(`[Mutation] Removing item ${variables.itemId} from translation queue`);
            queryClient.setQueryData(queryKey, (oldData) => 
              oldData?.filter(item => item.id !== variables.itemId) || []
            );
            
            // Update other related queues as well
            queryClient.setQueryData(['missingNutrients', schoolId], (oldData) => 
              data.queueUpdates.removeFromNutrients && oldData ? 
              oldData.filter(item => item.id !== variables.itemId) : oldData
            );
            
            queryClient.setQueryData(['missingTags', schoolId], (oldData) => 
              data.queueUpdates.removeFromTags && oldData ? 
              oldData.filter(item => item.id !== variables.itemId) : oldData
            );
          }
          
          console.log(`[Mutation] Cache updated with completion status for key: ${JSON.stringify(queryKey)}`);
        } else if (data?.updatedMissingItems && Array.isArray(data.updatedMissingItems)) {
          // Fallback: handle old response format for backwards compatibility
          console.log(`[Mutation] Received ${data.updatedMissingItems.length} updated missing items from backend (fallback mode).`);
          queryClient.setQueryData(queryKey, data.updatedMissingItems);
          console.log(`[Mutation] Cache updated with data from PUT response for key: ${JSON.stringify(queryKey)}`);
        } else {
            console.warn('[Mutation] PUT response did not contain expected queueUpdates or updatedMissingItems. Cache not updated directly.');
        }
    }
  });

  const handleSaveTranslations = useCallback(async () => {
    if (!selectedItem || !isAtLeastOneTranslationFilled() || saveMutation.isLoading) return;

    console.log(`[Save] Starting mutation for item ID: ${selectedItem.id}`);
    console.log(`[Save] Current item missing languages:`, selectedItem.missingLanguages);
    console.log(`[Save] Current translation state:`, translations);

    const translationsToSave = {};
    selectedItem.missingLanguages.forEach(langCode => {
      if (translations[langCode] && translations[langCode].trim()) {
        translationsToSave[langCode] = translations[langCode].trim();
      }
    });

    console.log(`[Save] Actual translations being sent to API:`, translationsToSave);

    if (Object.keys(translationsToSave).length === 0) {
       console.log(`[Save] No actual translations to save for missing languages. Aborting API call.`);
       return; 
    }

    const translationData = {
      itemId: selectedItem.id,
      itemType: selectedItem.type,
      translations: translationsToSave, 
      schoolId: schoolId,
      selectedLanguageCodes: selectedCodesString,
      userLanguage: i18n.language || 'en'
    };
    
    saveMutation.mutate(translationData);

  }, [selectedItem, translations, items, isAtLeastOneTranslationFilled, schoolId, selectedCodesString, i18n.language, queryClient, t, handleSelectItem, saveMutation, queryKey]);
  
  const handleSkipItem = useCallback(() => {
    if (!selectedItem || items.length <= 1) return;
    
    const currentIndex = items.findIndex(item => item.id === selectedItem.id);
    const nextIndex = (currentIndex + 1) % items.length;
    handleSelectItem(items[nextIndex]);
  }, [selectedItem, items, handleSelectItem]);
  
  const handleRetry = () => {
    console.log('[Retry] Refetching missing translations...');
    refetch();
  };
  
  const checkServerStatus = async () => {
    try {
      const response = await fetch(`${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001'}/api/status`);
      const data = await response.json();
      console.log('Server status:', data);
      alert(`Server status: ${data.status}\nUptime: ${Math.floor(data.uptime / 60)} minutes\nEnvironment: ${data.environment}`);
    } catch (err) {
      console.error('Error checking server status:', err);
      alert(`Server appears to be offline or unreachable: ${err.message}`);
    }
  };
  
  const handleBulkImportComplete = useCallback((result) => {
    console.log('[Bulk Import] Completed with result:', result);
    
    // Get the full query key, including selected codes
    const fullQueryKey = queryKey; // Use the memoized key from useQuery
    
    if (result?.success) {
      // Check if the backend returned the updated list
      if (result.updatedMissingItems && Array.isArray(result.updatedMissingItems)) {
          console.log(`[Bulk Import] Success! Updating cache for key ${JSON.stringify(fullQueryKey)} directly with ${result.updatedMissingItems.length} items from response.`);
          // Directly set the cache with the data from the POST response
          queryClient.setQueryData(fullQueryKey, result.updatedMissingItems);
          setActiveTab('individual'); // Switch back to individual tab
      } else {
          // Fallback: Invalidate if backend didn't return the list (or if param wasn't sent)
          console.warn('[Bulk Import] Success, but backend did not return updated items. Invalidating query cache as fallback...');
          queryClient.invalidateQueries({ queryKey: fullQueryKey })
            .then(() => {
              console.log('[Bulk Import] Fallback query cache invalidation successful.');
              setActiveTab('individual'); 
            })
            .catch(err => {
              console.error('[Bulk Import] Fallback error invalidating query cache:', err);
            });
      }
    } else {
      console.warn('[Bulk Import] Completed with errors, not updating cache automatically.', result?.error);
    }
  }, [queryClient, schoolId, queryKey, setActiveTab, selectedCodesString]); // Added queryKey and selectedCodesString to dependencies
  
  const handleOpenDeleteModal = useCallback(() => {
    if (!selectedItem) return;
    setDeleteModalOpen(true);
  }, [selectedItem]);
  
  const handleDeleteSuccess = useCallback(() => {
    console.log(`[Delete] Deletion successful for item ID: ${selectedItem?.id}. Invalidating query...`);
    // Invalidate both missing translations and the nutrient queue
    queryClient.invalidateQueries({ queryKey: ['missingTranslations', schoolId] });
    queryClient.invalidateQueries({ queryKey: ['nutrientQueue', schoolId] });
      
    // Clear selection after invalidation
    setSelectedItem(null); 
    
  }, [selectedItem?.id, queryClient, schoolId]);
  
  return (
    <>
      <Navbar isAdmin={true} />
      <div className="max-w-7xl mx-auto p-4">
        <h1 className="text-3xl font-bold mb-6">{t('admin.translationQueue')}</h1>
        {initError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">{t('general.error')}</p>
            <p>{initError}</p>
          </div>
        )}
        <div className="bg-white rounded-lg overflow-hidden mb-6">
        
          {!initError && !loading && !fetchError && schoolId && (
            <div className="bg-white rounded-lg overflow-hidden">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  <button
                    className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                      activeTab === 'individual'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('individual')}
                  >
                    {t('translations.individualTranslations')}
                  </button>
                  <button
                    className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                      activeTab === 'bulk'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('bulk')}
                  >
                    {t('translations.bulkTranslations')}
                  </button>
                </nav>
              </div>
              
              {/* Bulk Translation Section - only shown when bulk tab is active */}
              {activeTab === 'bulk' && (
                <BulkTranslationSection 
                  schoolId={getSchoolId()}
                  selectedLanguages={selectedLanguages}
                  userLanguage={i18n.language || 'en'}
                  onImportComplete={handleBulkImportComplete}
                />
              )}
            </div>
          )}
          
          {loading && (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <LoadingSpinner size="large" />
                <p className="mt-4 text-gray-600">Loading translation items...</p>
              </div>
            </div>
          )}
          
          {!initError && isError && fetchError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p className="font-bold">{t('general.error')}</p>
              <p>{fetchError.message || 'Failed to fetch missing translations'}</p>
              <div className="mt-3 flex space-x-4">
                <button 
                  onClick={handleRetry} 
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  {t('general.retry')}
                </button>
                <button 
                  onClick={checkServerStatus} 
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                >
                  {t('general.checkServerStatus')}
                </button>
              </div>
            </div>
          )}
          
          {!initError && !loading && !isError && items.length === 0 && activeTab === 'individual' && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              <p className="font-bold">{t('admin.noMissingTranslations')}</p>
              <p>{t('admin.allTranslationsComplete')}</p>
            </div>
          )}
          
          {/* Individual Translation Interface - only shown when individual tab is active */}
          {!initError && !loading && !isError && items.length > 0 && activeTab === 'individual' && (
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left sidebar - Items to translate */}
              <div className="w-full md:w-1/3 bg-white p-4 border-r border-gray-200">
                <h2 className="text-xl font-semibold mb-3">{t('admin.itemsToTranslate')} ({items.length})</h2>
                <p className="text-sm text-gray-600 mb-4">
                  {t('admin.progress')}: {stats.completed}/{stats.total} ({Math.round((stats.completed / stats.total) * 100) || 0}%)
                </p>
                
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {items.map(item => (
                    <div 
                      key={`${item.type}-${item.id}`}
                      className={`p-3 rounded cursor-pointer transition-colors ${
                        selectedItem && selectedItem.id === item.id ? 
                        'bg-blue-100 border-l-4 border-blue-500' : 
                        'hover:bg-gray-100 border-l-4 border-transparent'
                      }`}
                      onClick={() => handleSelectItem(item)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{getDisplayName(item)}</span>
                          <p className="text-xs text-gray-500">{item.type === 'dish' ? t('admin.dish') : t('admin.specialName')}</p>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {item.missingLanguages.map(lang => (
                            <span key={lang} className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                              {lang.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Right content - Translation form */}
              <div className="w-full md:w-2/3 bg-white pr-4 py-4">
                {selectedItem ? (
                  <>
                    <h2 className="text-xl font-semibold mb-3">
                      {selectedItem.type === 'dish' ? t('admin.dish') : t('admin.specialName')}: {getDisplayName(selectedItem)}
                    </h2>
                    <p className="text-sm text-gray-600 mb-4">
                      {t('admin.missingLanguages')}: {selectedItem.missingLanguages.map(lang => getLanguageName(lang)).join(', ')}
                    </p>
                    
                    {saveMutation.isSuccess && (
                      <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                        {t('admin.translationsSaved')}
                      </div>
                    )}
                    
                    {saveMutation.isError && (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        <p className="font-bold">{t('general.error')}</p>
                        <p>{saveMutation.error?.message || 'Failed to save translations'}</p>
                      </div>
                    )}
                    
                    <div className="space-y-4 mb-6">
                      {selectedItem.missingLanguages.map(lang => (
                        <div key={lang} className="form-group">
                          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor={`translation-${lang}`}>
                            {getLanguageName(lang)}
                          </label>
                          <input
                            id={`translation-${lang}`}
                            type="text"
                            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                              !translations[lang] ? 'border-yellow-300 bg-yellow-50' : ''
                            }`}
                            value={translations[lang] || ''}
                            onChange={(e) => handleTranslationChange(lang, e.target.value)}
                            placeholder={t('admin.enterTranslation')}
                          />
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-between">
                      <button
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                        onClick={handleSkipItem}
                      >
                        {t('admin.skipItem')}
                      </button>
                      <div className="space-x-2">
                        <button
                          className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                          onClick={handleOpenDeleteModal}
                        >
                          {t('modal.delete')}
                        </button>
                        <button
                          className={`${
                            isAtLeastOneTranslationFilled() && !saveMutation.isLoading
                              ? 'bg-blue-500 hover:bg-blue-700' 
                              : 'bg-blue-300 cursor-not-allowed'
                          } text-white font-bold py-2 px-4 rounded`}
                          onClick={handleSaveTranslations}
                          disabled={!isAtLeastOneTranslationFilled() || saveMutation.isLoading}
                        >
                          {saveMutation.isLoading ? t('saving') : t('admin.saveTranslations')}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-center items-center h-64 text-gray-500">
                    {items.length > 0 
                      ? t('admin.selectItemToTranslate')
                      : t('admin.noMoreItemsToTranslate')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Delete Dish Modal */}
      {selectedItem && (
        <DeleteDishModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onSuccess={handleDeleteSuccess}
          itemId={selectedItem?.id}
          itemType={selectedItem?.type || 'dish'}
          schoolId={getSchoolId()}
          language={i18n.language}
        />
      )}
    </>
  );
};

export default TranslationQueuePage; 